import { AlertCircle, CheckCircle2, ChevronLeft, ChevronRight, LoaderCircle, Search, X } from 'lucide-react';
import { useEffect } from 'react';

export function Logo({ light = false }: { light?: boolean }) {
  return <img className={`logo ${light ? 'logo--light' : ''}`} src={`/assets/brand-ci-20261005/beaw-${light ? 'white' : 'blue'}.png`} alt="Beaw — Be a reviewer" width={180} height={74} />;
}

export function Spinner({ label = 'กำลังโหลด' }: { label?: string }) {
  return <div className="state state--loading" role="status"><LoaderCircle className="spin" aria-hidden="true" /><span>{label}</span></div>;
}

export function ErrorState({ message, retry }: { message: string; retry?: () => void }) {
  return <div className="state state--error" role="alert"><AlertCircle aria-hidden="true" /><div><strong>โหลดข้อมูลไม่สำเร็จ</strong><p>{message}</p>{retry && <button className="button button--ghost" onClick={retry}>ลองอีกครั้ง</button>}</div></div>;
}

export function EmptyState({ title = 'ยังไม่มีข้อมูล', description = 'เมื่อมีรายการใหม่ ข้อมูลจะแสดงที่นี่' }: { title?: string; description?: string }) {
  return <div className="state state--empty"><CheckCircle2 aria-hidden="true" /><div><strong>{title}</strong><p>{description}</p></div></div>;
}

export function StatusBadge({ value }: { value?: unknown }) {
  const raw = String(value ?? 'unknown').trim();
  const normalized = raw.toLowerCase();
  const textMap: Record<string, string> = {
    non_verified: 'Not verified',
    phone_verified: 'Phone verified',
    verified_condition_a: 'Verified Creator',
    verified: 'Creator',
    pending_review: 'Pending review',
    more_info_requested: 'More info requested',
    needs_info: 'Needs info',
    rejected: 'Rejected',
    approved: 'Approved',
    confirmed: 'Confirmed',
    active: 'Active',
    submitted: 'Submitted',
    pending: 'Pending',
    processing: 'Processing',
    transferred: 'Transferred',
    paid: 'Paid',
    published: 'Published',
    member: 'Member',
    creator: 'Creator',
    merchant: 'Merchant',
  };

  const text = textMap[normalized] || raw.replaceAll('_', ' ');
  const tone = /approved|verified|confirmed|active|paid|transferred|published|creator/i.test(text) ? 'success' : /reject|archive|inactive|fail|rejected/i.test(text) ? 'danger' : /pending|request|process|review|submitted|more info|needs info/i.test(text) ? 'warning' : 'neutral';
  return <span className={`badge badge--${tone}`}>{text}</span>;
}

export function SearchBar({ value, onChange, placeholder = 'ค้นหา...' }: { value: string; onChange: (value: string) => void; placeholder?: string }) {
  return <label className="search"><Search size={18} aria-hidden="true" /><span className="sr-only">ค้นหา</span><input value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} /></label>;
}

export function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (page: number) => void }) {
  return <nav className="pagination" aria-label="Pagination"><button disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label="หน้าก่อนหน้า"><ChevronLeft /></button><span>หน้า {page} / {Math.max(1, totalPages)}</span><button disabled={page >= totalPages} onClick={() => onChange(page + 1)} aria-label="หน้าถัดไป"><ChevronRight /></button></nav>;
}

export function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  useEffect(() => {
    const close = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', close); return () => document.removeEventListener('keydown', close);
  }, [onClose]);
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}><section className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title"><header><h2 id="modal-title">{title}</h2><button className="icon-button" onClick={onClose} aria-label="ปิด"><X /></button></header>{children}</section></div>;
}

export function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return <label className="field"><span>{label}</span>{children}{hint && <small>{hint}</small>}</label>;
}

export function JsonDetails({ value }: { value: unknown }) {
  return <details className="json-details"><summary>ข้อมูลจาก Backend ทั้งหมด</summary><pre>{JSON.stringify(value, null, 2)}</pre></details>;
}
